<template>
  <div>
    <form-group label="Site template">
      <SiteTemplateSelection v-model="siteTemplate"/>
    </form-group>

    <h2 class="h2">Actor Profile Management</h2>

    <form-group :label="'Access level: ' + accessLevel">
      <checkbox v-model="generalPublisher.curation" label="Curation: owners must accept new actors"/>
    </form-group>

    <form-group label="Available actor types" v-if="publisher.viewActorTypes">
      <checkbox model-value="LegalEntity" label="Organization" inline disabled val="LegalEntity"/>
      <checkbox
        v-model="publisher.viewActorTypes" inline multiple val="Person"
        v-if="$parent.form.viewActorTypes.includes('Person')"
      />
      <checkbox
        v-model="publisher.viewActorTypes" inline multiple val="Product" :label="productLabel"
        v-if="$parent.form.viewActorTypes.includes('Product')"
      />
    </form-group>

    <div class="row">
      <div class="col-xs-12 col-md-6 col-lg-4 col-xl-3">
        <form-group :label="`How many Organizations can a user create`">
          <checkbox type="radio" v-model="createLegalEntityPolicy" val="none"/>
          <checkbox type="radio" v-model="createLegalEntityPolicy" val="one"/>
          <checkbox type="radio" v-model="createLegalEntityPolicy" val="many"/>
        </form-group>
        <span v-if="$parent.form.viewActorTypes.includes('Person')" class="actor-types__help-text">
          Person remains at 'one' and can be toggled off by using the checkboxes with the actor types
        </span>
      </div>
      <div v-if="$parent.form.viewActorTypes.includes('Product')" class="col-xs-12 col-md-6 col-lg-4 col-xl-3">
        <form-group :label="`How many ${productsLabel} can a user create`">
          <checkbox type="radio" v-model="createProductPolicy" val="none"/>
          <checkbox type="radio" v-model="createProductPolicy" val="one"/>
          <checkbox type="radio" v-model="createProductPolicy" val="many"/>
        </form-group>
      </div>
    </div>

    <template v-if="$store.getters.hasAccessToMonitoring">
      <h2 class="h2" style="margin-bottom: 0px;">Content</h2>
      <form-group label="Choose which type of article content can be viewed">
        <CuratedContentSelection v-model="publisher.monitoringContentMode"/>
      </form-group>
    </template>

    <template
      v-if="publisher.viewDashboard && publisher.viewDashboard.includes('knowledge-base') && $store.getters.isNewKnowledgeBaseEnabled"
    >
      <h2 class="h2" style="margin-bottom: 0px;">Knowledge Base</h2>
      <form-group label="View content">
        <checkbox type="radio" v-model="knowledgeBaseViewType" val="none" label="None"/>
        <checkbox type="radio" v-model="knowledgeBaseViewType" val="view_preview" label="View Preview"/>
        <checkbox type="radio" v-model="knowledgeBaseViewType" val="view_detail" label="View Detail"/>
        <checkbox type="radio" v-model="knowledgeBaseViewType" val="all" label="Everything"/>
      </form-group>

      <form-group label="Upload Content">
        <checkbox label="Allow to upload content" v-model="canUploadContentToKnowledgeBase" inline :val="true"/>
        <checkbox label="Curation: owners must accept uploaded resources" v-model="shouldCurateContentToKnowledgeBase" inline :val="true"/>
      </form-group>
    </template>

    <template v-if="$store.getters.areChallengesEnabled">
      <h2 class="h2">{{ challengesLabel }}</h2>

      <checkbox v-model="publisher.canCreateChallenge" :label="`Can create a ${challengeLabel}`" :val="true"/>
      <checkbox v-model="publisher.canCreatePrivateChallenge" :label="`Can choose to make their ${challengesLabel} private`" :val="true" :disabled="!publisher.canCreateChallenge"/>
      <checkbox
        :disabled="!publisher.canCreateChallenge"
        v-model="publisher.challengeMatchmakingEnabled"
        :label="`Allow them to use matchmaking for their ${challengesLabel}`"
        :val="true"
        v-if="isDeveloper"
      />
      <br/>
      <form-group :label="`View ${challengesLabel}, except for the detail pages`">
        <checkbox v-model="publisher.viewableChallengeStatusses" inline multiple val="new"/>
        <checkbox v-model="publisher.viewableChallengeStatusses" inline multiple val="open"/>
        <checkbox v-model="publisher.viewableChallengeStatusses" inline multiple val="solved"/>
      </form-group>

      <form-group :label="`View ${challengeLabel} details`">
        <checkbox v-model="publisher.accessibleChallengeStatusses" inline multiple val="new"/>
        <checkbox v-model="publisher.accessibleChallengeStatusses" inline multiple val="open"/>
        <checkbox v-model="publisher.accessibleChallengeStatusses" inline multiple val="solved"/>
      </form-group>
    </template>

    <h2 class="h2">{{ announcementsAndEventsLabel }}</h2>
    <checkbox v-model="canCreateAnnouncement" :label="`Can create ${announcementsAndEventsLabel}`" :disabled="!areMessageBoardsEnabled"/>
    <checkbox v-model="canViewPublicAnnouncements" :label="`Can view ${announcementsAndEventsLabel}`" :disabled="!areMessageBoardsEnabled"/>
    <checkbox
      v-model="shouldCurateContentToAnnouncements"
      :label="`Curation: owners must accept created ${announcementsAndEventsLabel}`"
      inline
      :val="true"
      :disabled="!areMessageBoardsEnabled"
    />
    <br/><br/>

    <h2 class="h2">Views</h2>
    <template v-if="siteTemplate === SiteTemplate.TRADITIONAL">
      <form-group label="Homepage - Buzz">
        <checkbox v-model="enableFundingBuzz" label="Display the latest funding rounds."/>
        <checkbox v-model="enableEventsBuzz" label="Display the latest events from your ecosystem."/>
        <checkbox v-model="enableInfluencersBuzz" label="Display the most influential actors from your ecosystem."/>
      </form-group>

      <form-group label="Directory views">
        <checkbox v-model="publisher.viewList" label="Show the list page"/>
        <checkbox v-model="publisher.viewGallery" label="Show the gallery page"/>
        <checkbox v-model="publisher.viewMap" label="Show the map page"/>
      </form-group>
    </template>

    <form-group label="Actor Profile">
      <checkbox v-model="publisher.views" multiple val="actors/detail" label="View full actor detail"/>
      <checkbox v-model="canViewActorConnections" label="View actor connections"/>
    </form-group>

    <form-group
      label="Actor Reports - Grant access to actor reports of the actor types selected below"
      style="width: 30rem;"
    >
      <dropdown :options="selectableActorTypes" v-model="viewReportOfActorTypes" :multiple="true"/>
    </form-group>

    <template v-if="siteTemplate === SiteTemplate.TRADITIONAL">
      <form-group label="List view columns">
        <dropdown v-model="publisher.viewList" multiple :options="viewListOptions"/>
      </form-group>
    </template>

    <form-group label="Enabled dashboards">
      <dropdown multiple v-model="publisher.viewDashboard" :options="viewDashboardOptions"/>
    </form-group>

    <h2 class="h2">Directory settings</h2>

    <form-group label="Legend property">
      <dropdown v-model="publisher.defaultLegendProperty" :options="legendPropertyOptions"/>
    </form-group>

    <form-group :label="publisher.defaultLegendProperty">
      <dropdown
        multiple v-model="publisher.availableLegendItems[publisher.defaultLegendProperty]"
        :options="getLegendOptions(publisher.defaultLegendProperty, publisher)"
      />
    </form-group>

    <form-group label="Available filter controls for actors" style="margin-top: 10px;">
      <dropdown
        v-model="publisher.filterControls" :options="availableFilterControlOptionsActor" multiple
        @update:modelValue="onUpdateActorFilterControls"
      />
    </form-group>

    <form-group
      label="Configure what filter controls are visible on the directory and on the actor page"
      style="margin-top: 10px;"
    >
      <dropdown
        multiple
        class="dropdown-with-hardcoded-sdg"
        :model-value="publisher.simplifiedFilterControls ? ['sustainability_goal', ...publisher.simplifiedFilterControls] : ['sustainability_goal']"
        :options="visibleFilterControlOptionsActor"
        @update:modelValue="publisher.simplifiedFilterControls = $event.filter(value => value !== 'sustainability_goal')"
      />
    </form-group>

    <form-group v-if="siteTemplate === SiteTemplate.NEW_SIMPLIFIED">
      <checkbox
        v-model="publisher.allowAllFilters"
        :label="`Show \'more search options\' on Simplified Actors overview`"
      />
    </form-group>

    <template v-if="publisher.viewActorTypes && publisher.viewActorTypes.includes('Product')">
      <h2 class="h2">{{ productLabel }} Gallery settings</h2>

      <form-group label="Legend property">
        <dropdown v-model="productLegendProperty" :options="legendPropertyOptions"/>
      </form-group>

      <form-group :label="productLegendProperty" v-if="productLegendProperty">
        <dropdown
          multiple v-model="availableProductLegendItems"
          :options="getLegendOptions(productLegendProperty, publisher)"
        />
      </form-group>
      <form-group label="Available filter controls for products" style="margin-top: 10px;">
        <dropdown
          v-model="publisher.filterControlsProducts" :options="availableFilterControlOptionsProduct" multiple
          @update:modelValue="onUpdateProductFilterControls"
        />
      </form-group>
      <form-group
        label="Configure what filter controls are visible on the product gallery and on the product page"
        style="margin-top: 10px;"
      >
        <dropdown
          :model-value="publisher.simplifiedFilterControlsProducts ? ['sustainability_goal', ...publisher.simplifiedFilterControlsProducts] : ['sustainability_goal']"
          :options="visibleFilterControlOptionsProduct"
          @update:modelValue="publisher.simplifiedFilterControlsProducts = $event.filter(value => value !== 'sustainability_goal')"
          multiple class="dropdown-with-hardcoded-sdg"
        />
      </form-group>

      <form-group v-if="siteTemplate === SiteTemplate.NEW_SIMPLIFIED">
        <checkbox
          v-model="publisher.allowAllFiltersProducts"
          :label="`Show \'more search options\' on Simplified ${productsLabel} overview`"
        />
      </form-group>
    </template>

    <h3>Accessible data</h3>

    <form-group label="Configure what actors can be accessed by selecting one or more portfolios">
      <!--<label class="form-group__header">
        <div class="form-group__label__text">
          <Icon name="information-outline" size="18" style="vertical-align: sub;"/>
          In order to display the actors of the accessible spotting areas, make sure to add their linked portfolios, as defined on
          <router-link to="/settings/spotting-areas">
            Spotting Areas
          </router-link>
        </div>
      </label>-->
      <AutocompleteTagInput
        :tags="selectedPortfolios"
        :options="portfolioOptions"
        :addOnlyFromAutocomplete="true"
        placeholder="Search for a portfolio"
        @tagChanged="updatePortfolioOptions"
        @input:raw="updateSelectedPortfolios"
        :minInputLength="1"
        style="width: 440px;"
      />
    </form-group>

    <template v-if="$store.getters.hasAccessToNewSpottingAreas && $store.getters.isSpottingAreaScopeEnabled">
      <form-group
        label="Configure what spotting areas & actors can be accessed by selecting one or more spotting areas"
      >
        <label class="form-group__header">
          <div class="form-group__label__text">
            <Icon name="information-outline" size="18" style="vertical-align: sub;"/>
            In order to display the spotting areas, make sure to set their access to 'public' defined on
            <router-link to="/settings/spotting-areas">
              Spotting Areas
            </router-link>
          </div>
        </label>
        <AutocompleteTagInput
          :tags="selectedSpottingAreas"
          :options="spottingAreaOptions"
          :addOnlyFromAutocomplete="true"
          placeholder="Search for a spotting area"
          @tagChanged="updateSpottingAreaOptions"
          @input:raw="updateSelectedAccessibleSpottingAreas"
          :minInputLength="1"
          style="width: 440px;"
        />
      </form-group>
    </template>

    <div class="row">
      <div class="col-xs-12 col-sm-6">
        <h4>Hidden data</h4>
        <p>
          <small>This user role does <b>NOT</b> have access to the following.</small>
        </p>
        <div class="property-group" v-for="g in privatePropertyGroups" @click.prevent="pushPublic(g.properties)">
          <div>
            {{ g.label }}
          </div>
          <div class="property-group__list">
            {{ g.properties.join(', ') }}
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-sm-6">
        <h4>Visible data</h4>
        <p>
          <small>This user role has access to the following.</small>
        </p>
        <h2 v-if="!publisher.public_fields || !publisher.public_fields.length">Click on a data point on
          the left to make it visible for ecosystem members.</h2>
        <div
          class="property-group" v-for="g in publicPropertyGroups"
          :class="{ 'property-group--disabled': g.label === 'Minimum' }" @click.prevent="splicePublic(g.properties)"
        >
          <div>
            {{ g.label }}
            <small v-if="g.label === 'Minimum'">must be published</small>
          </div>
          <div class="property-group__list">
            {{ g.properties.join(', ') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import AutocompleteTagInput from '../../Form/AutocompleteTagInput.vue'
  import Checkbox from '../../Form/Checkbox.vue'
  import Dropdown from '../../Dropdown/Dropdown.vue'
  import FormGroup from '../../Form/FormGroup.vue'
  import CountryDropdown from '../../Dropdown/CountryDropdown.vue'
  import SiteTemplateSelection from './SiteTemplateSelection.vue'

  import { fetchPortfolio } from '../../../api/portfolios'

  import ConfigMixin from '../../../util/ConfigMixin'
  import PermissionsMixin from '../../../util/PermissionsMixin'
  import { SiteTemplate, SPOTTING_AREA_TYPES } from '../../../store/modules/config'

  import debounce from 'lodash/throttle'
  import toArray from 'lodash/toArray'
  import difference from 'lodash/difference'

  import { propertyGroups as rawPropertyGroups, viewDashboardOptions, viewListOptions } from '../../../constants/config'
  import flatten from 'lodash/flatten'
  import CuratedContentSelection from './CuratedContentSelection.vue'
  import TranslationsMixin from '../../../util/TranslationsMixin.js'
  import { fetchSpottingAreas } from '@/api/spottingareas'

  export default {
    name: 'ActorPermissions',
    props: {
      generalPublisher: {
        type: Object,
      },
      selectedPortfolios: {
        type: Array,
      },
      selectedSpottingAreas: {
        type: Array,
      },
      publisher: { // Ecosystem member permissions are stored in "publisher"
        type: Object,
      },
    },
    emits: ['updateAccessibleSpottingAreas', 'updatePortfolios'],
    data () {
      return {
        SiteTemplate,
        portfolioOptions: [],
        spottingAreaOptions: [],
      }
    },
    computed: {
      availableFilterControlOptionsProduct () {
        var eligibleFilterControlOptions = [...this.filterControlOptionsProduct]

        if (this.viewReportOfActorTypes && this.viewReportOfActorTypes.length > 0 && this.viewReportOfActorTypes.includes('Product')) {
          eligibleFilterControlOptions.push({ value: 'reports', label: 'Reports' })
        }

        return eligibleFilterControlOptions
      },
      availableFilterControlOptionsActor () {
        var eligibleFilterControlOptions = [...this.filterControlOptionsActor]

        if (this.viewReportOfActorTypes && this.viewReportOfActorTypes.length > 0) {
          eligibleFilterControlOptions.push({ value: 'reports', label: 'Reports' })
        }

        return eligibleFilterControlOptions
      },
      isDeveloper () {
        return this.$store.getters.isDeveloper
      },
      selectableActorTypes () {
        return this.publisher.viewActorTypes ? this.publisher.viewActorTypes.map(type => {
          const actorTypeLabel = type === 'Product' ? this.productLabel : (type === 'LegalEntity' ? 'Organization' : type)

          return { label: actorTypeLabel, value: type }
        }) : []
      },
      canCreateAnnouncement: {
        get () {
          if (!this.publisher.hasOwnProperty('canCreateAnnouncement')) {
            return true
          }

          return !!this.publisher.canCreateAnnouncement
        },
        set (value) {
          if (!this.publisher.hasOwnProperty('canCreateAnnouncement')) {
            this.publisher.canCreateAnnouncement = value
          }

          this.publisher.canCreateAnnouncement = value
        },
      },
      canViewActorConnections: {
        get () {
          if (!this.publisher.hasOwnProperty('canViewActorConnections')) {
            this.publisher.canViewActorConnections = false
          }

          return this.publisher.canViewActorConnections
        },
        set (value) {
          if (!this.publisher.hasOwnProperty('canViewActorConnections')) {
            this.publisher.canViewActorConnections = value
          }

          this.publisher.canViewActorConnections = value
        },
      },
      viewReportOfActorTypes: {
        get () {
          if (!this.publisher.hasOwnProperty('viewReportOfActorTypes')) {
            this.publisher.viewReportOfActorTypes = []
          }

          return this.publisher.viewReportOfActorTypes
        },
        set (value) {
          if (!this.publisher.hasOwnProperty('viewReportOfActorTypes')) {
            this.publisher.viewReportOfActorTypes = value
          }

          this.publisher.viewReportOfActorTypes = value
        },
      },
      accessLevel () {
        if (this.generalPublisher.allow_anonymous) {
          return 'public'
        }

        return this.generalPublisher.allowAdd ? 'login required' : 'private'
      },
      siteTemplate: {
        get () {
          return this.publisher.siteTemplate || SiteTemplate.TRADITIONAL
        },
        set (value) {
          if (!this.publisher.siteTemplate) {
            this.publisher.siteTemplate = ''
          }

          if (value === SiteTemplate.SIMPLIFIED || value === SiteTemplate.NEW_SIMPLIFIED) {
            if (!this.publisher.views) {
              this.publisher.views = []
            }

            if (!this.publisher.views.includes('actors/detail')) {
              this.publisher.views.push('actors/detail')
            }
          }

          this.publisher.siteTemplate = value
        },
      },
      visibleFilterControlOptionsActor () {
        if (!this.publisher || !this.publisher.filterControls) {
          return [{ value: 'sustainability_goal', label: 'Sustainability Goals' }]
        }

        let defaultFilters = [
          { value: 'sustainability_goal', label: 'Sustainability Goals' }
        ]

        if (this.publisher.filterControls.includes('reports')) {
          defaultFilters.push({ value: 'reports', label: 'Reports' })
        }

        return [
          ...defaultFilters,
          ...this.publisher.filterControls.map((control) => {
            return this.translatedFilterControlOptions.find(filterOption => {
              return filterOption.value === control
            })
          })
            .filter((control) => {
              if (!control) {
                return
              }

              // Only allow choosing options that are available on the simplified actors page
              return control.value !== 'sustainability_goal' && this.translatedSimplifiedFilterControlOptions.find(filterOption => {
                return filterOption.value === control.value
              })
            })]
      },
      createLegalEntityPolicy: {
        get () {
          return this.publisher.createLegalEntityPolicy || 'one'
        },
        set (value) {
          if (!this.publisher.createLegalEntityPolicy) {
            this.publisher.createLegalEntityPolicy = 'one'
          }

          this.publisher.createLegalEntityPolicy = value
        },
      },
      createProductPolicy: {
        get () {
          return this.publisher.createProductPolicy || 'many'
        },
        set (value) {
          if (!this.publisher.createProductPolicy) {
            this.publisher.createProductPolicy = 'many'
          }

          this.publisher.createProductPolicy = value
        },
      },
      productLegendProperty: {
        get () {
          return this.publisher.productLegendProperty || null
        },
        set (value) {
          if (!this.publisher.productLegendProperty) {
            this.publisher.productLegendProperty = ''
          }
          this.publisher.productLegendProperty = value
        },
      },
      canUploadContentToKnowledgeBase: {
        get () {
          return !!this.publisher.canUploadContentToKnowledgeBase
        },
        set (value) {
          if (!this.publisher.canUploadContentToKnowledgeBase) {
            this.publisher.canUploadContentToKnowledgeBase = true
          }

          this.publisher.canUploadContentToKnowledgeBase = !!value
        },
      },
      shouldCurateContentToKnowledgeBase: {
        get () {
          return !!this.publisher.shouldCurateContentToKnowledgeBase
        },
        set (value) {
          if (!this.publisher.shouldCurateContentToKnowledgeBase) {
            this.publisher.shouldCurateContentToKnowledgeBase = true
          }

          this.publisher.shouldCurateContentToKnowledgeBase = !!value
        },
      },
      shouldCurateContentToAnnouncements: {
        get () {
          if (this.publisher.shouldCurateContentToAnnouncements == undefined) {
            return true
          }

          return !!this.publisher.shouldCurateContentToAnnouncements
        },
        set (value) {
          if (!this.publisher.shouldCurateContentToAnnouncements) {
            this.publisher.shouldCurateContentToAnnouncements = true
          }
          this.publisher.shouldCurateContentToAnnouncements = !!value
        },
      },
      knowledgeBaseViewType: {
        get () {
          return this.publisher.knowledgeBaseViewType || 'all'
        },
        set (value) {
          if (!this.publisher.knowledgeBaseViewType) {
            this.publisher.knowledgeBaseViewType = 'all'
          }

          this.publisher.knowledgeBaseViewType = value
        },
      },
      availableProductLegendItems: {
        get () {
          if (this.publisher.availableLegendItems) {
            return this.publisher.availableLegendItems[this.productLegendProperty] || []
          }
          return []
        },
        set (value) {
          if (!this.publisher.availableLegendItems) {
            this.publisher.availableLegendItems = {}
          }

          if (!this.publisher.availableLegendItems[this.productLegendProperty]) {
            this.publisher.availableLegendItems[this.productLegendProperty] = []
          }

          this.publisher.availableLegendItems[this.productLegendProperty] = value
        },
      },
      canCreateChallenge: {
        get () {
          return this.publisher.canCreateChallenge || false
        },
        set (value) {
          if (!this.publisher.canCreateChallenge) {
            this.publisher.canCreateChallenge = false
          }

          this.publisher.canCreateChallenge = value
        },
      },
      canCreatePrivateChallenge: {
        get () {
          return this.publisher.canCreatePrivateChallenge || false
        },
        set (value) {
          if (!this.publisher.canCreatePrivateChallenge) {
            this.publisher.canCreatePrivateChallenge = false
          }

          this.publisher.canCreatePrivateChallenge = value
        },
      },
      challengeMatchmakingEnabled: {
        get () {
          return this.publisher.challengeMatchmakingEnabled || false
        },
        set (value) {
          if (!this.publisher.challengeMatchmakingEnabled) {
            this.publisher.challengeMatchmakingEnabled = false
          }

          this.publisher.challengeMatchmakingEnabled = value
        },
      },
      viewableChallengeStatusses: {
        get () {
          return this.publisher.viewableChallengeStatusses || []
        },
        set (value) {
          if (!this.publisher.viewableChallengeStatusses) {
            this.publisher.viewableChallengeStatusses = []
          }

          this.publisher.viewableChallengeStatusses = value
        },
      },
      accessibleChallengeStatusses: {
        get () {
          return this.publisher.accessibleChallengeStatusses || []
        },
        set (value) {
          if (!this.publisher.accessibleChallengeStatusses) {
            this.publisher.accessibleChallengeStatusses = []
          }

          this.publisher.accessibleChallengeStatusses = value
        },
      },
      canViewPublicAnnouncements: {
        get () {
          if (!this.publisher.hasOwnProperty('canViewPublicAnnouncements')) {
            this.publisher.canViewPublicAnnouncements = true
          }

          return !!this.publisher.canViewPublicAnnouncements
        },
        set (v) {
          if (!this.publisher.hasOwnProperty('canViewPublicAnnouncements')) {
            this.publisher.canViewPublicAnnouncements = v
          }

          this.publisher.canViewPublicAnnouncements = v
        },
      },
      areMessageBoardsEnabled () {
        return this.$store.getters.areMessageBoardsEnabled
      },
      viewListOptions () {
        if (!this.publicFields.includes('completeness')) {
          return viewListOptions.filter(opt => opt !== 'completeness')
        }

        return viewListOptions
      },
      visibleFilterControlOptionsProduct () {
        if (!this.publisher || !this.publisher.filterControlsProducts) {
          return [{ value: 'sustainability_goal', label: 'Sustainability Goals' }]
        }
        return [{
          value: 'sustainability_goal',
          label: 'Sustainability Goals',
        }, ...this.publisher.filterControlsProducts.map((control) => {
          return this.translatedFilterControlOptions.find(filterOption => {
            return filterOption.value === control
          })
        }).filter((control) => {
          if (!control) {
            return
          }

          // Only allow choosing options that are available on the simplified products page
          return control.value !== 'sustainability_goal' && this.translatedSimplifiedFilterControlOptions.find(filterOption => {
            return filterOption.value === control.value
          })
        })]
      },
      /*visibleFilterControlOptionsActor () {
        if (!this.publisher || !this.publisher.filterControls) {
          return [{ value: 'sustainability_goal', label: 'Sustainability Goals' }]
        }
        return [{
          value: 'sustainability_goal',
          label: 'Sustainability Goals',
        }, ...this.publisher.filterControls.map((control) => {
          return this.translatedFilterControlOptions.find(filterOption => {
            return filterOption.value === control
          })
        }).filter((control) => {
          if (!control) {
            return
          }

          // Only allow choosing options that are available on the simplified actors page
          return control.value !== 'sustainability_goal' && this.translatedSimplifiedFilterControlOptions.find(filterOption => {
            return filterOption.value === control.value
          })
        })]
      },*/
      fillableFields () {
        return flatten(toArray(this.propertyGroups))
      },
      propertyGroups () {
        const propertyGroups = rawPropertyGroups

        delete propertyGroups.ContactDetails

        return propertyGroups
      },
      publicFields () {
        if (this.publisher && this.publisher.public_fields) {
          return this.publisher.public_fields
        }
        return []
      },
      privateFields () {
        if (this.publisher && this.publisher.public_fields) {
          return difference(this.fillableFields, this.publicFields)
        }

        return this.fillableFields
      },
      publicPropertyGroups () {
        return this.filterPropertyGroups(property => this.publicFields.indexOf(property) !== -1)
      },
      privatePropertyGroups () {
        return this.filterPropertyGroups(property => this.privateFields.indexOf(property) !== -1)
      },
      viewDashboardOptions () {
        var options = this.$parent.$parent.$parent.form.viewDashboard && viewDashboardOptions.filter(d => this.$parent.$parent.$parent.form.viewDashboard.includes(d.value)) || []
        options = options.filter(d => !['message_dashboard', 'buzz', 'media-buzz', 'connections'].includes(d.value))

        return options
      },
      enableFundingBuzz: {
        get () {
          // Make sure legacy settings where cards are collapsed remain open
          if (this.generalPublisher.enableFundingBuzz == undefined) {
            return true
          }

          return this.generalPublisher.enableFundingBuzz
        },
        set (v) {
          this.generalPublisher.enableFundingBuzz = v
        },
      },
      enableEventsBuzz: {
        get () {
          // Make sure legacy settings where cards are collapsed remain open
          if (this.generalPublisher.enableEventsBuzz == undefined) {
            return true
          }

          return this.generalPublisher.enableEventsBuzz
        },
        set (v) {
          this.generalPublisher.enableEventsBuzz = v
        },
      },
      enableInfluencersBuzz: {
        get () {
          // Make sure legacy settings where cards are collapsed remain open
          if (this.generalPublisher.enableInfluencersBuzz == undefined) {
            return true
          }

          return this.generalPublisher.enableInfluencersBuzz
        },
        set (v) {
          this.generalPublisher.enableInfluencersBuzz = v
        },
      },
    },
    methods: {
      onUpdateActorFilterControls () {
        // filter to only allow available controls
        if (this.publisher.simplifiedFilterControls) {
          this.publisher.simplifiedFilterControls = this.publisher.simplifiedFilterControls.filter(filterControl => filterControl !== 'sustainability_goal' && this.visibleFilterControlOptionsActor.find(filterControlOption => filterControlOption.value === filterControl))
        }
      },
      onUpdateProductFilterControls () {
        // filter to only allow available controls
        if (this.publisher.simplifiedFilterControlsProducts) {
          this.publisher.simplifiedFilterControlsProducts = this.publisher.simplifiedFilterControlsProducts.filter(filterControl => filterControl !== 'sustainability_goal' && this.visibleFilterControlOptionsProduct.find(filterControlOption => filterControlOption.value === filterControl))
        }
      },
      filterPropertyGroups (func) {
        // Returns a mapped and filtered array of property groups
        return Object.keys(this.propertyGroups)
          .filter(p => p !== 'Competitive Scores' || this.$parent.$parent.$parent.form.hasScores)
          .map(label => ({
            label,
            properties: this.propertyGroups[label].filter(func),
          })).filter(g => g.properties && g.properties.length)
      },
      updatePortfolioOptions: debounce(async function (query) {
        if (!query) {
          return
        }

        // This will search for a portfolio which matches the input value in the search field
        var filters = Object.assign({}, { query: query })

        fetchPortfolio(filters)
          .then(portfolios => {
            this.portfolioOptions = []

            this.parsePortfolioOptions(portfolios)
          }).catch(errors => {
          console.log(errors)
        })
      }, 150),
      parsePortfolioOptions (portfolios) {
        if (!Array.isArray(portfolios)) {
          return
        }

        var options = []

        portfolios.forEach(portfolio => {
          if (portfolio.virtual) {
            return
          }

          options.push({
            text: portfolio.name,
            id: portfolio.id,
          })
        })

        this.portfolioOptions = options
      },
      updateSelectedPortfolios (input) {
        this.$emit('updatePortfolios', input)
      },
      updateSpottingAreaOptions: debounce(async function (query) {
        if (!query) {
          return
        }

        // This will search for a portfolio which matches the input value in the search field
        var filters = Object.assign({}, { query: query })

        fetchSpottingAreas(filters)
          .then(spottingAreas => {
            this.spottingAreaOptions = []

            this.parseSpottingAreaOptions(spottingAreas)
          }).catch(errors => {
          console.log(errors)
        })
      }, 150),
      parseSpottingAreaOptions (spottingAreas) {
        if (!Array.isArray(spottingAreas)) {
          return
        }

        var options = []

        spottingAreas.forEach(spottingArea => {
          let showEye = true
          let invalidOption = spottingArea.type === SPOTTING_AREA_TYPES.PREMIUM || spottingArea.type === SPOTTING_AREA_TYPES.PRIVATE

          options.push({
            text: spottingArea.name,
            id: spottingArea.id,
            showEye: showEye,
            invalidOption: invalidOption,
          })
        })

        this.spottingAreaOptions = options
      },
      updateSelectedAccessibleSpottingAreas (input) {
        var inputValues = []

        input.forEach(spottingArea => {
          inputValues.push({
            text: spottingArea.text,
            id: spottingArea.id,
            optionValue: spottingArea.optionValue,
          })
        })

        this.$emit('updateAccessibleSpottingAreas', inputValues)
      },
      pushPublic (f) {
        if (!this.publisher.public_fields) {
          this.publisher.public_fields = []
        }
        if (Array.isArray(f)) {
          f.forEach(this.pushPublic)
          return
        }
        if (this.publisher.public_fields.indexOf(f) < 0) {
          this.publisher.public_fields.push(f)
        }
      },
      // @param 1 or an array of properties
      splicePublic (f) {
        if (Array.isArray(f)) {
          f.forEach(this.splicePublic)
          return
        }
        if (this.publisher.public_fields.indexOf(f) >= 0) {
          this.publisher.public_fields.splice(this.publisher.public_fields.indexOf(f), 1)
        }
      },
    },
    watch: {
      canCreateChallenge (value) {
        if (!value) {
          // Can't create a private challenge if you lack the basic "can create a challenge"
          this.publisher.canCreatePrivateChallenge = false
          this.publisher.challengeMatchmakingEnabled = false
        }
      },
      viewReportOfActorTypes (val) {
        if (val && val.length > 0) {
          return
        }

        if (this.publisher.filterControlsProducts) {
          this.publisher.filterControlsProducts = this.publisher.filterControlsProducts.filter(filter => filter !== 'reports')
        }

        if (this.publisher.filterControls) {
          this.publisher.filterControls = this.publisher.filterControls.filter(filter => filter !== 'reports')
        }
      },
    },
    mounted () {
      this.onUpdateProductFilterControls()
      this.onUpdateActorFilterControls()
    },
    components: {
      CuratedContentSelection,
      AutocompleteTagInput,
      Checkbox,
      Dropdown,
      FormGroup,
      CountryDropdown,
      SiteTemplateSelection,
    },
    mixins: [ConfigMixin, PermissionsMixin, TranslationsMixin],
  }
</script>
<style scoped lang="scss">
  @import "../../../../scss/_variables.scss";

  .actor-types__help-text {
    display: flex;
    font-size: 12px;
    color: $color-text-grey-light;
    margin-bottom: 0.5rem;
  }
</style>
